import React from 'react'; // eslint-disable-line

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Card, ContentCard, Dropdown, DropdownItem, Filter, FilterSetController, Icon, Status, StopPropagation, Table, ToastContext, TooltipHelp } from '@weezevent/nacre';

import { useCurrentPageFilters } from '../../../../../utils/filters';
import { handleSearchQuery } from '../../../../manager/components/context';
import ModerationPaginator from '../../../components/moderationPaginator';
import ConfirmModal from '../../../../../components/confirmModal';
import LoadingTable from '../../../../../components/loadingTable';
import { capitalizeFirstLetter } from '../../../../attendee/detail/components/utils';
import { ModerationTransitionConditions } from '../../../../../models';

const spec = {
    multiple: ['type', 'active'],
    fields: ['search'],
    aliases: {},
    date: [],
    form: []
};

export default function ConditionsTab({ organization, flowId }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const history = useHistory();
    const [currentPage, setCurrentPage] = React.useState(0);
    const [transitionConditionToDelete, setTransitionConditionToDelete] = React.useState(false);
    const [pageSize] = React.useState(100);
    const [filters, setFilters] = useCurrentPageFilters(spec);
    const defaultFilters = {};

    const [transitionConditions, loadingTransitionConditions, syncTransitionConditions] = ModerationTransitionConditions.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            cache: false,
            allow_cache: false,
            query: {
                limit: pageSize,
                offset: currentPage * pageSize,
                ...handleSearchQuery(spec, filters)
            }
        },
        [organization, flowId, filters, currentPage, handleSearchQuery]
    );

    const handleSearch = React.useCallback(
        value => {
            setFilters({ ...filters, search: value });
        },
        [filters]
    );
    const handleStatus = React.useCallback(
        (condition, fn) => {
            condition[fn]({ organizationId: organization.id, flowId }).then(() => syncTransitionConditions());
        },
        [syncTransitionConditions, organization.id, flowId]
    );

    const typesFilterOptions = React.useCallback(() => {
        if (!transitionConditions) {
            return [];
        }
        const mapToOptions = transitionConditions.map(condition => {
            //TODO this will be drop when we'll introduce a transition condition manager
            // like the one we have for actions.
            const cond = condition.toOption(flowId);
            cond.text = t(`ticket.moderation.statusesTransitions.transitionConditionsList.conditionTypes.${cond.text}`);
            return cond;
        });
        return mapToOptions;
    }, [transitionConditions, flowId]);

    const onDelete = React.useCallback(
        transition => {
            transition
                .delete({ organizationId: organization.id, flowId })
                .then(() => {
                    toast.success(t(`ticket.moderation.statusesTransitions.transitionConditionsList.actionDelete.toastSuccess`));
                    setTransitionConditionToDelete(false);
                    syncTransitionConditions();
                })
                .catch(() => {
                    toast.error(t(`ticket.moderation.statusesTransitions.transitionConditionsList.actionDelete.toastError`));
                });
        },
        [organization, flowId, syncTransitionConditions]
    );

    return (
        <Card>
            <ContentCard>
                <FilterSetController
                    total={transitionConditions?.length}
                    isLoadingTotal={loadingTransitionConditions}
                    labelTotal={t('ticket.moderation.statusesTransitions.transitionConditionsList.countTotal', { count: transitionConditions?.length || 0 })}
                    onChange={values => setFilters(values)}
                    initialValues={filters}
                    defaultValues={defaultFilters}
                    onReset={() => {}}
                    search={{
                        onChange: (_, search = '') => handleSearch(search),
                        handleReset: () => setFilters(filters => ({ ...filters, search: '' })),
                        placeholder: t('ticket.moderation.common.searchPlaceholder'),
                        value: filters?.search
                    }}
                >
                    <Filter index={0} name="type" options={typesFilterOptions()} labelName={t('ticket.moderation.statusesTransitions.transitionConditionsList.transitionType')} />
                    <Filter
                        index={1}
                        name="active"
                        options={[
                            { key: 'true', name: 'true', text: t('ticket.moderation.statusesTransitions.transitionConditionsList.conditionStatus.conditionStatusActive') },
                            { key: 'false', name: 'false', text: t('ticket.moderation.statusesTransitions.transitionConditionsList.conditionStatus.conditionStatusInactive') }
                        ]}
                        labelName={t('ticket.moderation.statusesTransitions.transitionConditionsList.conditionStatus.label')}
                    />
                </FilterSetController>
            </ContentCard>
            <Table useNew>
                <Table.Header>
                    <Table.Tr>
                        <th>{t('ticket.moderation.statusesTransitions.transitionConditionsList.name')}</th>
                        <th>{t('ticket.moderation.statusesTransitions.transitionConditionsList.transitionType')}</th>
                        <th>
                            {t('ticket.moderation.statusesTransitions.transitionConditionsList.transitionsNumber.label')}
                            <TooltipHelp title={t('ticket.moderation.statusesTransitions.transitionConditionsList.transitionsNumber.tooltip')} />
                        </th>
                        <th>{t('ticket.moderation.statusesTransitions.transitionConditionsList.conditionStatus.label')}</th>
                        <th>{t('ticket.moderation.statusesTransitions.transitionConditionsList.actions')}</th>
                    </Table.Tr>
                </Table.Header>
                {!!transitionConditions?.length && !loadingTransitionConditions && (
                    <Table.TBody>
                        {transitionConditions?.map(condition => (
                            <ConditionRow
                                key={condition.id}
                                condition={condition}
                                organization={organization}
                                handleStatus={handleStatus}
                                flowId={flowId}
                                setTransitionConditionToDelete={setTransitionConditionToDelete}
                            />
                        ))}
                    </Table.TBody>
                )}
            </Table>
            <LoadingTable loading={loadingTransitionConditions} height="30vh" />
            {!loadingTransitionConditions && !transitionConditions?.length && (
                <Card.EmptyState
                    title={t('ticket.moderation.statusesTransitions.transitionConditionsList.emptyTitle')}
                    icon={<Icon name="list" />}
                    subtitle={t('ticket.moderation.statusesTransitions.transitionConditionsList.emptySubtitle')}
                    labelButton={t('ticket.moderation.statusesTransitions.transitionConditionsList.emptyCta')}
                    onClick={() => history.push('conditions/new')}
                />
            )}
            {transitionConditionToDelete && (
                <ConfirmModal
                    labelTitle={t('ticket.moderation.statusesTransitions.transitionConditionsList.actionDelete.modalTitle')}
                    labelBody={t('ticket.moderation.statusesTransitions.transitionConditionsList.actionDelete.modalDescription')}
                    objectToSave={transitionConditionToDelete}
                    onClose={() => setTransitionConditionToDelete(false)}
                    onAccept={onDelete}
                />
            )}
            <ModerationPaginator
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalCount={+transitionConditions?.__meta?.headers?.get('total-count') || 0}
                pageSize={pageSize}
            />
        </Card>
    );
}
const ConditionRow = ({ condition, organization, handleStatus, flowId, setTransitionConditionToDelete }) => {
    const { t } = useTranslation();
    const history = useHistory();

    return (
        <Table.Tr onClick={() => history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/conditions/${condition.id}`)}>
            <Table.Td>{capitalizeFirstLetter(condition?.name) || '-'}</Table.Td>
            <Table.Td>{t(`ticket.moderation.statusesTransitions.transitionConditionsList.conditionTypes.${condition?.type}`)}</Table.Td>
            <Table.Td>{condition?.transitions?.length || 0}</Table.Td>
            <Table.Td>
                <StopPropagation>
                    <Status.Dropdown
                        color={!condition.active ? 'gray' : 'green'}
                        items={[
                            <DropdownItem
                                key="0"
                                disabled={condition.active}
                                onClick={() => handleStatus(condition, 'activate')}
                                item={t('ticket.moderation.statusesTransitions.transitionConditionsList.conditionStatus.conditionStatusActive')}
                            />,
                            <DropdownItem
                                key="1"
                                disabled={!condition.active}
                                onClick={() => handleStatus(condition, 'deactivate')}
                                item={t('ticket.moderation.statusesTransitions.transitionConditionsList.conditionStatus.conditionStatusInactive')}
                            />
                        ]}
                        text={t(`ticket.moderation.statusesTransitions.transitionConditionsList.conditionStatus.conditionStatus${condition.active ? 'Active' : 'Inactive'}`)}
                    />
                </StopPropagation>
            </Table.Td>

            <Table.Td>
                <Dropdown closeOnDocumentClick size="medium">
                    <StopPropagation>
                        <DropdownItem
                            item={t('ticket.moderation.statusesTransitions.transitionConditionsList.actionEdit')}
                            onClick={() => history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/conditions/${condition.id}`)}
                        />
                        <DropdownItem
                            deleteItem
                            onClick={() => setTransitionConditionToDelete(condition)}
                            item={t('ticket.moderation.statusesTransitions.transitionConditionsList.actionDelete.title')}
                        />
                    </StopPropagation>
                </Dropdown>
            </Table.Td>
        </Table.Tr>
    );
};
